import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import LineGraph from '@/shared/components/ui/LineGraph';
import MinimalisticDropDown from '@/shared/components/ui/MinimalisticDropDown';

import {
  fetchOutcomeStepEventsApi,
  type StepOutcomeEvent,
  type ListStepOutcomeEventsParams,
} from '@/features/system/orchestrator/api/outcomeEventsApi';

type GroupMode = 'worker_handler' | 'worker_handler_params';

const DEFAULT_WINDOW_DAYS = 7;
const BUCKET_MS = 60 * 60 * 1000; // 1 hour
const MAX_EVENTS = 5000;

function toIso(ms: number): string {
  return new Date(ms).toISOString();
}

function floorToBucket(ms: number, bucketMs: number): number {
  return Math.floor(ms / bucketMs) * bucketMs;
}

function parseDateMs(v: any): number | null {
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s) return null;
  const d = new Date(s);
  const t = d.getTime();
  return Number.isFinite(t) ? t : null;
}

function tryParseJson(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  const t = v.trim();
  if (!t) return v;
  if (
    (t.startsWith('{') && t.endsWith('}'))
    || (t.startsWith('[') && t.endsWith(']'))
    || (t.startsWith('"') && t.endsWith('"'))
  ) {
    try {
      return JSON.parse(t);
    } catch {
      return v;
    }
  }
  return v;
}

function stableStringify(value: unknown, depth = 3): string {
  const seen = new WeakSet<object>();

  const prune = (v: any, d: number): any => {
    if (d <= 0) return null;
    if (v == null) return v;

    if (typeof v === 'string') {
      const s = v.length > 220 ? `${v.slice(0, 219)}…` : v;
      return s;
    }
    if (typeof v === 'number' || typeof v === 'boolean') return v;

    const parsed = tryParseJson(v);
    if (parsed !== v) return prune(parsed, d);

    if (Array.isArray(v)) {
      return v.slice(0, 8).map((x) => prune(x, d - 1));
    }

    if (typeof v === 'object') {
      if (seen.has(v as any)) return '[Circular]';
      seen.add(v as any);

      const obj = v as Record<string, any>;
      const keys = Object.keys(obj).sort();
      const out: Record<string, any> = {};
      for (const k of keys.slice(0, 24)) {
        out[k] = prune(obj[k], d - 1);
      }
      return out;
    }

    try {
      return String(v);
    } catch {
      return '';
    }
  };

  try {
    return JSON.stringify(prune(value, depth));
  } catch {
    try {
      return String(value);
    } catch {
      return '';
    }
  }
}

function hashString(s: string): string {
  // Small non-crypto hash for grouping keys.
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16);
}

function extractTokenCount(value: unknown): number | null {
  const v = tryParseJson(value);

  if (typeof v === 'number' && Number.isFinite(v)) return v;

  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  if (!v || typeof v !== 'object') return null;

  const anyV: any = v as any;

  const direct = [
    anyV.total_tokens,
    anyV.totalTokens,
    anyV.tokens,
    anyV.token_count,
    anyV.tokenCount,
  ].find((x: any) => typeof x === 'number' && Number.isFinite(x));

  if (typeof direct === 'number' && Number.isFinite(direct)) return direct;

  const usage = anyV.usage;
  if (usage && typeof usage === 'object') {
    const u: any = usage;
    const tot = u.total_tokens ?? u.totalTokens;
    if (typeof tot === 'number' && Number.isFinite(tot)) return tot;

    const pt = u.prompt_tokens ?? u.promptTokens;
    const ct = u.completion_tokens ?? u.completionTokens;
    if (typeof pt === 'number' && typeof ct === 'number' && Number.isFinite(pt) && Number.isFinite(ct)) return pt + ct;
  }

  return null;
}

async function fetchOutcomeEventsPaged(params: ListStepOutcomeEventsParams, maxItems: number): Promise<StepOutcomeEvent[]> {
  let cursor_created_at: string | undefined = params.cursor_created_at;
  let cursor_outcome_id: number | undefined = params.cursor_outcome_id;

  const items: StepOutcomeEvent[] = [];
  for (let page = 0; page < 20; page++) {
    const res = await fetchOutcomeStepEventsApi({
      ...params,
      cursor_created_at,
      cursor_outcome_id,
    });

    const pageItems = Array.isArray((res as any)?.items) ? (res as any).items as StepOutcomeEvent[] : [];
    items.push(...pageItems);

    if (items.length >= maxItems) break;

    const next = (res as any)?.next_cursor ?? null;
    const nextCreated = String(next?.created_at ?? '').trim();
    const nextOutcomeId = typeof next?.outcome_id === 'number' ? next.outcome_id : null;

    if (!nextCreated || nextOutcomeId == null) break;

    cursor_created_at = nextCreated;
    cursor_outcome_id = nextOutcomeId;
  }

  return items.slice(0, maxItems);
}

function pickText(v: any): string {
  const s = String(v ?? '').trim();
  return s || '—';
}

export default function WorkflowStatsPanel() {
  const [groupMode, setGroupMode] = useState<GroupMode>('worker_handler');

  const now = Date.now();
  const fromMs = now - DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const toMs = now;

  const query = useQuery({
    queryKey: ['developer', 'workflowStats', 'stepOutcomeEvents', { from: floorToBucket(fromMs, BUCKET_MS), to: floorToBucket(toMs, BUCKET_MS) }] as const,
    queryFn: async () => {
      return await fetchOutcomeEventsPaged({
        include_history: true,
        from: toIso(fromMs),
        to: toIso(toMs),
        limit: 1000,
      }, MAX_EVENTS);
    },
    staleTime: 10_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });

  const events: StepOutcomeEvent[] = Array.isArray(query.data) ? query.data : [];

  const { timeline, groups, hasAnyTokens, totals, truncated } = useMemo(() => {
    const createdMs = events
      .map((e) => parseDateMs((e as any).created_at))
      .filter((t): t is number => typeof t === 'number' && Number.isFinite(t))
      .sort((a, b) => a - b);

    const effectiveFrom = floorToBucket(createdMs[0] ?? fromMs, BUCKET_MS);
    const effectiveTo = floorToBucket(createdMs[createdMs.length - 1] ?? toMs, BUCKET_MS);

    const bucketCount = Math.max(1, Math.min(2000, Math.floor((effectiveTo - effectiveFrom) / BUCKET_MS) + 1));

    const buckets = new Map<number, { events: number; tokens: number }>();

    let anyTokens = false;
    let totalEvents = 0;
    let totalTokens = 0;

    const groupMap = new Map<string, {
      worker_key: string;
      handler_key_base: string;
      handler_namespace: string;
      params_preview: string;
      events: number;
      tokens: number;
    }>();

    for (const e of events) {
      const t = parseDateMs((e as any).created_at);
      if (t == null) continue;

      totalEvents++;

      const token = extractTokenCount((e as any).value);
      if (token != null && token > 0) {
        anyTokens = true;
        totalTokens += token;
      }

      const b = floorToBucket(t, BUCKET_MS);
      const prev = buckets.get(b) ?? { events: 0, tokens: 0 };
      prev.events += 1;
      if (token != null && token > 0) prev.tokens += token;
      buckets.set(b, prev);

      const worker_key = pickText((e as any).worker_key);
      const handler_key_base = pickText((e as any).handler_key_base);
      const handler_namespace = pickText((e as any).handler_namespace);

      const paramsPreview = stableStringify((e as any).value, 3);
      const paramsSig = hashString(paramsPreview);

      const key = (
        groupMode === 'worker_handler'
          ? `${worker_key}|${handler_key_base}|${handler_namespace}`
          : `${worker_key}|${handler_key_base}|${handler_namespace}|${paramsSig}`
      );

      const g = groupMap.get(key) ?? {
        worker_key,
        handler_key_base,
        handler_namespace,
        params_preview: paramsPreview,
        events: 0,
        tokens: 0,
      };

      g.events += 1;
      if (token != null && token > 0) g.tokens += token;

      groupMap.set(key, g);
    }

    const timelineRows: Array<{ ts: string; events: number; tokens: number }> = [];
    for (let i = 0; i < bucketCount; i++) {
      const ms = effectiveFrom + i * BUCKET_MS;
      const v = buckets.get(ms) ?? { events: 0, tokens: 0 };
      timelineRows.push({ ts: toIso(ms), events: v.events, tokens: v.tokens });
    }

    const groupRows = Array.from(groupMap.values())
      .sort((a, b) => {
        const primary = (anyTokens ? b.tokens - a.tokens : b.events - a.events);
        if (primary !== 0) return primary;
        return (b.events - a.events);
      })
      .slice(0, 50);

    return {
      timeline: timelineRows,
      groups: groupRows,
      hasAnyTokens: anyTokens,
      totals: { totalEvents, totalTokens },
      truncated: events.length >= MAX_EVENTS,
    };
  }, [events, fromMs, toMs, groupMode]);

  const metricKey = hasAnyTokens ? 'tokens' : 'events';
  const metricLabel = hasAnyTokens ? 'Total tokens' : 'Total events';

  return (
    <div className="h-full min-h-0 rounded-md border border-neutral-800 flex flex-col">
      <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between gap-3">
        <div className="text-sm text-neutral-200">Workers / Handlers</div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-neutral-500">Group by</div>
          <MinimalisticDropDown<GroupMode>
            value={groupMode}
            onChange={(v) => setGroupMode((v as GroupMode) || 'worker_handler')}
            options={[
              { value: 'worker_handler', label: 'worker + handler' },
              { value: 'worker_handler_params', label: 'worker + handler + parameters' },
            ]}
            minWidth={240}
          />
        </div>
      </div>

      <div className="p-3 flex-1 min-h-0 overflow-auto space-y-4">
        <div className="text-xs text-neutral-500">
          Window: last {DEFAULT_WINDOW_DAYS} days. Timeline is hourly.
          {truncated ? <span className="text-amber-300"> (truncated to {MAX_EVENTS} events)</span> : null}
        </div>

        {query.isLoading ? (
          <div className="text-sm text-neutral-400">Loading events…</div>
        ) : query.error ? (
          <div className="text-sm text-rose-400">Failed to load events: {(query.error as any)?.message ?? String(query.error)}</div>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-md border border-neutral-800 p-3">
                <div className="text-xs text-neutral-500">Events</div>
                <div className="text-lg text-neutral-100">{totals.totalEvents.toLocaleString()}</div>
              </div>
              <div className="rounded-md border border-neutral-800 p-3">
                <div className="text-xs text-neutral-500">Tokens (if detected)</div>
                <div className="text-lg text-neutral-100">{totals.totalTokens.toLocaleString()}</div>
              </div>
              <div className="rounded-md border border-neutral-800 p-3">
                <div className="text-xs text-neutral-500">Metric</div>
                <div className="text-lg text-neutral-100">{metricLabel}</div>
              </div>
            </div>

            <div className="rounded-md border border-neutral-800 p-3">
              <div className="text-xs text-neutral-400 mb-2">Usage timeline</div>
              <LineGraph
                data={timeline}
                xKey={'ts'}
                yKeys={[metricKey as any]}
                unit="hour"
                labels={[metricLabel]}
                height={220}
                showDots={false}
                showLegend={false}
              />
            </div>

            <div className="space-y-2">
              <div className="text-xs text-neutral-400">Top usage groups</div>
              {groups.length === 0 ? (
                <div className="text-sm text-neutral-600">No events in this window.</div>
              ) : (
                <div className="rounded-md border border-neutral-800 overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="border-b border-neutral-800">
                      <tr>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Worker</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Handler base</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Namespace</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Events</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Tokens</th>
                        <th className="px-2 py-1 text-neutral-500 font-medium">Parameters (sample)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800">
                      {groups.map((g, idx) => (
                        <tr key={`${g.worker_key}:${g.handler_key_base}:${g.handler_namespace}:${idx}`} className="hover:bg-white/[0.02] align-top">
                          <td className="px-2 py-1 text-neutral-200 max-w-[14rem]"><div className="truncate" title={g.worker_key}>{g.worker_key}</div></td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[14rem]"><div className="truncate" title={g.handler_key_base}>{g.handler_key_base}</div></td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[14rem]"><div className="truncate" title={g.handler_namespace}>{g.handler_namespace}</div></td>
                          <td className="px-2 py-1 text-neutral-300 font-mono whitespace-nowrap">{g.events.toLocaleString()}</td>
                          <td className="px-2 py-1 text-neutral-300 font-mono whitespace-nowrap">{g.tokens.toLocaleString()}</td>
                          <td className="px-2 py-1 text-neutral-300 font-mono max-w-[34rem]"><div className="truncate" title={g.params_preview}>{g.params_preview}</div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
